import {
  Box,
  Button as ChakraButton,
  Input as ChakraInput,
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { IconChevronDownSmall } from 'central-icons/IconChevronDownSmall';
import { IconCircleInfo } from 'central-icons/IconCircleInfo';
import {
  type ComponentProps,
  forwardRef,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import NumberFormat, { type NumberFormatValues } from 'react-number-format';
import {
  components,
  type GroupBase,
  type OptionProps,
  type StylesConfig,
} from 'react-select';

import { Select } from '@revelio/ui';

import {
  BEA_INDUSTRIES,
  SECTORS_IN_ORDER,
  sectorForBea,
} from '../data/bea-industries';
import type { Scenario, SelectedGeo } from '../types';
import { GeoTreePicker } from './GeoTreePicker';

interface SelectOption {
  value: string;
  label: string;
  /** NAICS codes behind this BEA industry — searchable, shown on the row tooltip. */
  naics: string[];
}

const INDUSTRY_OPTIONS: SelectOption[] = BEA_INDUSTRIES.map((i) => ({
  value: i.bea,
  label: i.label,
  naics: i.naics,
}));

// Group the flat 396 industries into the NAICS sectors (canonical order),
// dropping any sector that ends up empty. react-select renders a sticky heading
// per group and hides a group once all its options filter out.
const INDUSTRY_GROUPS: GroupBase<SelectOption>[] = (() => {
  const bySector = new Map<string, SelectOption[]>();
  for (const option of INDUSTRY_OPTIONS) {
    const sector = sectorForBea(option.value);
    const bucket = bySector.get(sector);
    if (bucket) bucket.push(option);
    else bySector.set(sector, [option]);
  }
  return SECTORS_IN_ORDER.map((label) => ({
    label,
    options: bySector.get(label) ?? [],
  })).filter((group) => group.options.length > 0);
})();

// Screen-reader label for an industry's codes (the visual tooltip shows BEA and
// NAICS on separate lines).
const industryCodeAria = (option: SelectOption): string =>
  option.naics.length > 0
    ? `BEA ${option.value}, NAICS ${option.naics.join(', ')}`
    : `BEA ${option.value}`;

// Match the typed query against the industry name (substring) OR either code
// system: BEA and NAICS both match on prefix, so "software", "511200", "5112"
// and "51121" all land on Software publishers.
const filterIndustryOption = (
  option: { label: string; value: string; data: SelectOption },
  rawInput: string
): boolean => {
  const query = rawInput.trim().toLowerCase();
  if (!query) return true;
  if (option.label.toLowerCase().includes(query)) return true;
  if (option.value.toLowerCase().startsWith(query)) return true;
  return option.data.naics.some((code) => code.startsWith(query));
};

// Figma scenario popover (node 1:669): uniform 36px fields with a hairline
// navy border, 2px radius, 12px navy text. Shared across the Industry
// select, Geography trigger and Investment input so they line up exactly.
const FIELD_NAVY = '#2d426a';
const FIELD_BORDER = '0.75px solid rgba(45, 66, 106, 0.2)';
const FIELD_PLACEHOLDER = 'rgba(45, 66, 106, 0.45)';

const industrySelectStyles: StylesConfig<SelectOption, false> = {
  control: (base) => ({
    ...base,
    minHeight: '36px',
    height: '36px',
    border: FIELD_BORDER,
    borderRadius: '2px',
    boxShadow: 'none',
    cursor: 'pointer',
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  singleValue: (base) => ({ ...base, color: FIELD_NAVY, fontSize: '12px' }),
  input: (base) => ({ ...base, color: FIELD_NAVY, fontSize: '12px' }),
  placeholder: (base) => ({
    ...base,
    color: FIELD_PLACEHOLDER,
    fontSize: '12px',
  }),
  indicatorsContainer: (base) => ({ ...base, padding: 0 }),
  dropdownIndicator: (base) => ({
    ...base,
    color: FIELD_NAVY,
    padding: '0 6px',
  }),
  option: (base) => ({ ...base, fontSize: '12px', padding: '6px 10px' }),
  group: (base) => ({ ...base, paddingTop: 0, paddingBottom: '4px' }),
  // Compact, muted, sticky sector headers so the grouping reads as quiet
  // section dividers rather than competing with the industry rows.
  groupHeading: (base) => ({
    ...base,
    margin: 0,
    padding: '8px 10px 4px',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: FIELD_PLACEHOLDER,
    position: 'sticky',
    top: 0,
    background: 'white',
    zIndex: 1,
  }),
  // Let the dropdown breathe wider than the 256px popover so long industry
  // names and sector headings don't wrap; the menu is portaled, so it widens
  // independently of the popover.
  menu: (base) => ({ ...base, width: '360px', minWidth: '360px' }),
  // Match the filter dropdowns' scrollbar (FilterSelectDropdown) so the menu is
  // consistent with the rest of the app.
  menuList: (base) => ({
    ...base,
    maxHeight: '240px',
    scrollbarWidth: 'auto',
    scrollbarColor: '#e3e3e3 #ffffff',
    '&::-webkit-scrollbar': {
      width: '14px',
    },
  }),
};

// 10px semibold navy @ 70% — the Figma field label, with an optional info hint
// rendered inline to its right.
function FieldLabel({
  children,
  hint,
}: {
  children: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <Flex align="center" gap="4px" mb="6px">
      <Text fontSize="10px" fontWeight="600" color="navyBlue.500" opacity={0.7}>
        {children}
      </Text>
      {hint}
    </Flex>
  );
}

// react-number-format needs a forwardRef input; wrap the styled Chakra Input so
// the investment field formats as the user types ($ + thousands separators),
// matching the Talent Discovery pay filter.
const InvestmentInput = forwardRef<
  HTMLInputElement,
  ComponentProps<typeof ChakraInput>
>((props, ref) => (
  <ChakraInput
    ref={ref}
    h="36px"
    fontSize="12px"
    color="navyBlue.500"
    pl="8px"
    border={FIELD_BORDER}
    borderRadius="2px"
    bg="white"
    _placeholder={{ color: FIELD_PLACEHOLDER }}
    _hover={{ borderColor: 'rgba(45, 66, 106, 0.2)' }}
    _focusVisible={{
      borderColor: 'rgba(45, 66, 106, 0.45)',
      boxShadow: 'none',
    }}
    {...props}
  />
));
InvestmentInput.displayName = 'InvestmentInput';

// A small info affordance: an info icon that reveals tooltip content on hover,
// portaled to the body so it escapes the react-select menu's scroll container
// (Chakra's Tooltip can't portal in this version and would be clipped here).
// The z-index clears the portaled menu (9999); mirrors useTooltipPortal's
// body-portal approach.
function InfoHint({
  ariaLabel,
  content,
  size = 13,
}: {
  ariaLabel: string;
  content: ReactNode;
  size?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null
  );
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setCoords({ top: r.top + r.height / 2, left: r.right + 8 });
  };
  const hide = () => setCoords(null);
  return (
    <Box
      ref={ref}
      as="span"
      display="inline-flex"
      flexShrink={0}
      color={FIELD_PLACEHOLDER}
      _hover={{ color: FIELD_NAVY }}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      aria-label={ariaLabel}
      role="img"
    >
      <IconCircleInfo size={size} />
      {coords &&
        createPortal(
          <Box
            position="fixed"
            top={`${coords.top}px`}
            left={`${coords.left}px`}
            transform="translateY(-50%)"
            maxW="260px"
            px="9px"
            py="7px"
            bg="navyBlue.500"
            color="white"
            fontSize="11px"
            lineHeight="16px"
            borderRadius="6px"
            boxShadow="0 6px 18px rgba(45,66,106,0.3)"
            pointerEvents="none"
            zIndex={10000}
          >
            {content}
          </Box>,
          document.body
        )}
    </Box>
  );
}

// Option rows show the industry name only. The info icon (and its BEA/NAICS
// tooltip) appears only while the row is highlighted — hover or keyboard — so
// it's absent from the resting list; hovering the icon then reveals the codes.
function IndustryOption(
  props: OptionProps<SelectOption, false, GroupBase<SelectOption>>
) {
  const { data, isFocused } = props;
  return (
    <components.Option {...props}>
      <Flex align="center" justify="space-between" gap="8px" w="100%">
        <Text fontSize="12px" color={FIELD_NAVY} noOfLines={2}>
          {data.label}
        </Text>
        {isFocused && (
          <InfoHint
            ariaLabel={industryCodeAria(data)}
            content={
              <>
                <Box>BEA {data.value}</Box>
                {data.naics.length > 0 && (
                  <Box>NAICS {data.naics.join(', ')}</Box>
                )}
              </>
            }
          />
        )}
      </Flex>
    </components.Option>
  );
}

export type ScenarioDraft = Pick<
  Scenario,
  'industry' | 'industryId' | 'amount' | 'geo'
>;

const industryOptionFor = (industryId: string): SelectOption | null =>
  INDUSTRY_OPTIONS.find((o) => o.value === industryId) ?? null;

interface ScenarioPopoverProps {
  /** Popover trigger (the "+ Scenario" button or a scenario chip). */
  children: ReactNode;
  /** Existing scenario to edit; absent → add mode. */
  initial?: Scenario;
  /** Primary geo to default a NEW scenario's geography to. */
  defaultGeo?: SelectedGeo | null;
  submitLabel: string;
  confirmTestId: string;
  onSubmit: (draft: ScenarioDraft) => void;
  /**
   * Optional controlled open state. Pass both to drive the popover from a
   * parent (e.g. variant 1 opens it when a geography is picked on the map);
   * omit both to use the trigger's own click-to-toggle behaviour.
   */
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * When set, the form is shown read-only with this message banner at the top
   * (variants 2-4 use it so "+ Scenario" explains the first scenario must be
   * filled in before another can be added).
   */
  disabledReason?: string;
}

// Shared Industry · Geography · Investment form. Add mode starts from the
// page's primary geo; edit mode prefills from the scenario being edited.
export function ScenarioPopover({
  children,
  initial,
  defaultGeo = null,
  submitLabel,
  confirmTestId,
  onSubmit,
  isOpen: controlledOpen,
  onOpenChange,
  disabledReason,
}: ScenarioPopoverProps) {
  const disabled = !!disabledReason;
  const internal = useDisclosure();
  const isOpen = controlledOpen ?? internal.isOpen;
  const onOpen = () => (onOpenChange ? onOpenChange(true) : internal.onOpen());
  const onClose = () =>
    onOpenChange ? onOpenChange(false) : internal.onClose();
  const [industry, setIndustry] = useState<SelectOption | null>(null);
  const [geo, setGeo] = useState<SelectedGeo | null>(null);
  const [amount, setAmount] = useState<number | undefined>(undefined);

  // Reset the form to the scenario's values (edit) or defaults (add) each
  // time the popover opens.
  useEffect(() => {
    if (!isOpen) return;
    if (initial) {
      setIndustry(
        industryOptionFor(initial.industryId) ?? {
          value: initial.industryId,
          label: initial.industry,
          naics: [],
        }
      );
      setGeo(initial.geo);
      setAmount(initial.amount);
    } else {
      setIndustry(null);
      setGeo(defaultGeo);
      // Empty by default — the user enters the investment amount themselves.
      setAmount(undefined);
    }
  }, [isOpen, initial, defaultGeo]);

  // The impact API rejects a non-positive investment, so gate submit on it.
  const canSubmit = industry !== null && geo !== null && (amount ?? 0) > 0;

  return (
    <Popover
      placement="bottom-start"
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      // Mount the form only while open — several chips each render one.
      isLazy
      // The nested geography tree picker portals its own popover; blur from
      // interacting with it must not dismiss the panel.
      closeOnBlur={false}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent
        w="256px"
        color="navyBlue.500"
        bg="white"
        border="none"
        borderRadius="8px"
        boxShadow="0px 6px 18px 0px rgba(45,66,106,0.3)"
        _focusVisible={{
          outline: 'none',
          boxShadow: '0px 6px 18px 0px rgba(45,66,106,0.3)',
        }}
        data-testid="scenario-panel"
        px="11px"
        pt="11px"
        pb="13px"
      >
        <Flex direction="column" gap="12px">
          {disabledReason && (
            <Flex
              align="center"
              gap="8px"
              px="10px"
              py="8px"
              bg="orange.50"
              border="1px solid"
              borderColor="orange.200"
              borderRadius="6px"
              data-testid="scenario-panel-warning"
            >
              <IconCircleInfo
                size={14}
                color="var(--chakra-colors-orange-500, #dd6b20)"
                style={{ flexShrink: 0 }}
              />
              <Text fontSize="11.5px" fontWeight="600" color="orange.800">
                {disabledReason}
              </Text>
            </Flex>
          )}
          <Box>
            <FieldLabel
              hint={
                <InfoHint
                  size={12}
                  ariaLabel="How to search industries"
                  content={
                    <>
                      <Box>
                        Search by industry name, or by BEA or NAICS code.
                      </Box>
                      <Box mt="4px">
                        Industries follow the U.S. BEA Benchmark Input-Output
                        classification — each maps to one or more NAICS codes.
                      </Box>
                    </>
                  }
                />
              }
            >
              Industry
            </FieldLabel>
            <Select<SelectOption>
              options={INDUSTRY_GROUPS}
              value={industry}
              onChange={(option) => setIndustry(option as SelectOption | null)}
              placeholder="Select industry..."
              size="sm"
              isDisabled={disabled}
              styles={industrySelectStyles}
              components={{ Option: IndustryOption }}
              filterOption={filterIndustryOption}
              maxMenuHeight={260}
              noOptionsMessage={() =>
                'No match — try an industry name, BEA, or NAICS code'
              }
            />
          </Box>

          {/* Geography stays in our build (the Figma frame sets it via the
              page title) — styled as the same 36px field as Industry. */}
          <Box>
            <FieldLabel>Geography</FieldLabel>
            <GeoTreePicker selectedGeo={geo} onSelect={setGeo}>
              <Flex
                as="button"
                type="button"
                align="center"
                justify="space-between"
                w="100%"
                h="36px"
                pl="8px"
                pr="6px"
                border={FIELD_BORDER}
                borderRadius="2px"
                bg="white"
                cursor={disabled ? 'not-allowed' : 'pointer'}
                opacity={disabled ? 0.5 : 1}
                pointerEvents={disabled ? 'none' : undefined}
                data-testid="scenario-geography"
              >
                <Text
                  fontSize="12px"
                  color={geo ? 'navyBlue.500' : FIELD_PLACEHOLDER}
                  noOfLines={1}
                >
                  {geo?.name ?? 'Select a state or county...'}
                </Text>
                <IconChevronDownSmall size={16} />
              </Flex>
            </GeoTreePicker>
          </Box>

          <Box>
            <FieldLabel>Investment Amount</FieldLabel>
            <NumberFormat
              customInput={InvestmentInput}
              thousandSeparator
              prefix="$"
              decimalScale={0}
              allowNegative={false}
              value={amount ?? ''}
              onValueChange={(values: NumberFormatValues) =>
                setAmount(values.floatValue)
              }
              placeholder="$1,000,000"
              disabled={disabled}
              data-testid="investment-amount-input"
            />
          </Box>
        </Flex>

        <Flex justify="flex-end" align="center" gap="8px" mt="14px">
          <ChakraButton
            variant="unstyled"
            h="auto"
            minW="0"
            fontSize="12px"
            fontWeight="400"
            color="navyBlue.500"
            opacity={0.5}
            _hover={{ opacity: 0.8 }}
            onClick={onClose}
          >
            Cancel
          </ChakraButton>
          <ChakraButton
            bg="green.500"
            color="white"
            fontSize="12px"
            fontWeight="600"
            letterSpacing="-0.24px"
            h="auto"
            minW="0"
            px="10px"
            py="6px"
            borderRadius="4.6px"
            _hover={{ bg: 'green.600' }}
            _active={{ bg: 'green.600' }}
            isDisabled={disabled || !canSubmit}
            data-testid={confirmTestId}
            onClick={() => {
              if (!industry || !geo || !amount || amount <= 0) return;
              onSubmit({
                industry: industry.label,
                industryId: industry.value,
                amount,
                geo,
              });
              onClose();
            }}
          >
            {submitLabel}
          </ChakraButton>
        </Flex>
      </PopoverContent>
    </Popover>
  );
}
